import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSearch, SearchResult } from '../contexts/SearchContext';

const GlobalSearchResults: React.FC = () => {
  const { searchTerm, searchResults, totalResults, clearSearch, clearSearchWithAnimation, isExiting } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // No need for useClickOutside - we handle clicks directly on the overlay

  const handleSectionClick = (result: SearchResult, event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Navigate first, then clear search
    if (location.pathname === result.path) {
      // Force a re-render by updating the location state
      navigate(result.path, { 
        state: { scrollToSection: result.sectionId },
        replace: true
      });
    } else {
      // Navigate to a different page
      navigate(result.path, { 
        state: { scrollToSection: result.sectionId } 
      });
    }

    // Clear search after navigation
    clearSearch();
  };

  // Show search results on all pages when there's a search term
  if (!searchTerm.trim()) {
    return null;
  }

  if (totalResults === 0) {
    return (
      <div className="search-results">
        <p className="no-results">No results found for "{searchTerm}"</p>
      </div>
    );
  }

  // Group results by page
  const resultsByPage = searchResults.reduce((acc, result) => {
    if (!acc[result.page]) {
      acc[result.page] = [];
    }
    acc[result.page].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  // Calculate global index for animation delays
  let globalIndex = 0;

  return (
    <div className="search-results-overlay" onClick={(e) => {
      // Only clear search if clicking directly on the overlay background
      if (e.target === e.currentTarget) {
        clearSearchWithAnimation();
      }
    }}>
      <div className={`search-results-container ${isExiting ? 'exiting' : ''}`} onClick={(e) => {
        e.stopPropagation();
      }}>
        <div className="search-results-content">
          {Object.entries(resultsByPage).map(([page, results]) => (
            <div key={page} className="search-results-page">
              <h4 className="search-results-page-title">
                <Link to={results[0].path} className="search-results-page-link">
                  {page}
                </Link>
              </h4>
              <div className="search-results-sections">
                {results.map((result, index) => {
                  const currentIndex = globalIndex++;
                  return (
                    <div 
                      key={index} 
                      className="search-results-section search-results-section-clickable"
                      style={{
                        animationDelay: `${currentIndex * 0.1}s`
                      }}
                      onClick={(e) => {
                        handleSectionClick(result, e);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSectionClick(result, e);
                        }
                      }}
                    >
                    <h5 className="search-results-section-title">{result.section}</h5>
                    <p className="search-results-section-content">
                      {highlightSearchTerm(result.content, searchTerm)}
                    </p>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to highlight search terms
const highlightSearchTerm = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="search-highlight">{part}</span>
    ) : part
  );
};

export default GlobalSearchResults;
